// src/utils/bracketValidator.ts
import { normalizeTeamNameForComparison } from './teamUtils';
import teamNameMap from '../data/teamNameMap.json';
import teamsData from '../data/teams.json'; // Import teams data

/**
 * Validates a known_winners.txt file content
 * Returns validation results with success flag and error message if any
 */
export const validateKnownWinnersFile = (winnersContent: string): { 
  isValid: boolean;
  errorMessage?: string;
} => {
  if (!winnersContent.trim()) {
    return { isValid: true }; // Empty content is valid (no winners yet)
  }

  // Parse the comma-separated list
  const winners = winnersContent.split(',').map(w => w.trim());
  
  // Check for valid team names
  const invalidTeams = findInvalidTeamNames(winners);
  if (invalidTeams.length > 0) {
    return {
      isValid: false,
      errorMessage: `Invalid team names found: ${invalidTeams.slice(0, 3).join(', ')}${invalidTeams.length > 3 ? '...' : ''}`
    };
  }

  // Check first 32 games against the expected teams
  const firstRoundErrors = validateFirstRound(winners);
  if (firstRoundErrors.length > 0) {
    return {
      isValid: false,
      errorMessage: firstRoundErrors[0] // Return the first error
    };
  }

  // Check for bracket consistency
  const { isValid, errorMessage } = validateBracketStructure(winners);
  return { isValid, errorMessage };
};

/**
 * Validates the first 32 games against the expected teams from teams.json
 */
function validateFirstRound(winners: string[]): string[] {
  const errors: string[] = [];
  const teams = teamsData as any[]; // Type assertion for imported teams data

  // Map teams by their ID for easier lookup
  const teamsById: Record<string, any> = {};
  teams.forEach(team => {
    teamsById[team.id] = team;
  });

  // Define the expected matchups for the first 32 games
  const expectedMatchups = [
    // South region (games 0-7)
    ['s1', 's16'], ['s8', 's9'], ['s5', 's12'], ['s4', 's13'],
    ['s6', 's11'], ['s3', 's14'], ['s7', 's10'], ['s2', 's15'],
    // East region (games 8-15)
    ['e1', 'e16'], ['e8', 'e9'], ['e5', 'e12'], ['e4', 'e13'],
    ['e6', 'e11'], ['e3', 'e14'], ['e7', 'e10'], ['e2', 'e15'],
    // West region (games 16-23)
    ['w1', 'w16'], ['w8', 'w9'], ['w5', 'w12'], ['w4', 'w13'],
    ['w6', 'w11'], ['w3', 'w14'], ['w7', 'w10'], ['w2', 'w15'],
    // Midwest region (games 24-31)
    ['m1', 'm16'], ['m8', 'm9'], ['m5', 'm12'], ['m4', 'm13'],
    ['m6', 'm11'], ['m3', 'm14'], ['m7', 'm10'], ['m2', 'm15']
  ];

  // Check each of the first 32 games
  for (let i = 0; i < Math.min(32, winners.length); i++) {
    const winner = winners[i];
    if (!winner || winner === 'xxx') continue; // Skip empty games

    // Get the expected teams for this game
    const matchup = expectedMatchups[i];
    if (!matchup) continue; // Skip if matchup not defined

    const team1 = teamsById[matchup[0]];
    const team2 = teamsById[matchup[1]];

    if (!team1 || !team2) {
      errors.push(`Game ${i+1}: Could not find expected teams for matchup ${matchup.join(' vs ')}`);
      continue;
    }

    // Normalize team names for comparison
    const normalizedWinner = normalizeTeamNameForComparison(winner);
    const normalizedTeam1 = normalizeTeamNameForComparison(team1.name);
    const normalizedTeam2 = normalizeTeamNameForComparison(team2.name);

    // Check if the winner is one of the expected teams
    if (normalizedWinner !== normalizedTeam1 && normalizedWinner !== normalizedTeam2) {
      errors.push(`Game ${i+1}: Winner "${winner}" is not one of the expected teams (${team1.name} or ${team2.name})`);
    }
  }

  return errors;
}

/**
 * Validates and cleans a knownWinners array
 * Returns a corrected array with invalid entries removed
 */
export const validateAndCleanKnownWinners = (
  knownWinners: string[]
): { 
  cleanedWinners: string[]; 
  isValid: boolean;
  errorMessage?: string;
} => {
  if (!knownWinners || knownWinners.length === 0) {
    return { cleanedWinners: [], isValid: true };
  }

  // Create a copy of the array to avoid modifying the original
  const cleanedWinners = [...knownWinners];
  
  // Filter out invalid entries before validation
  const validWinnersForValidation = cleanedWinners.filter(name => 
    name && typeof name === 'string' && name.trim() !== '' && name.trim() !== 'xxx'
  );
  
  // Check for valid team names first
  const invalidTeams = findInvalidTeamNames(validWinnersForValidation);
  
  if (invalidTeams.length > 0) {
    // Don't return early, just note the error and continue with cleaning
    const errorMessage = `Invalid team names found: ${invalidTeams.slice(0, 3).join(', ')}${invalidTeams.length > 3 ? '...' : ''}`;
    
    // Remove invalid team names
    invalidTeams.forEach(teamName => {
      const index = cleanedWinners.findIndex(winner => 
        winner && typeof winner === 'string' && 
        normalizeTeamNameForComparison(winner) === normalizeTeamNameForComparison(teamName)
      );
      if (index !== -1) {
        cleanedWinners[index] = '';
      }
    });
    
    return { 
      cleanedWinners: fixBracketInconsistencies(cleanedWinners), 
      isValid: false,
      errorMessage 
    };
  }

  // Fix any bracket structure issues
  const fixedWinners = fixBracketInconsistencies(cleanedWinners);
  
  // Check if any changes were made
  const isValid = JSON.stringify(fixedWinners) === JSON.stringify(knownWinners);
  
  return { 
    cleanedWinners: fixedWinners, 
    isValid,
    errorMessage: isValid ? undefined : 'Some teams picked from the scenario have been eliminated. Please check games and repick winners\n\nTo clear this error, you can choose new winners and re-save this scenario or re-save the scenario without choosing new winners.'
  };
};

/**
 * Checks if all team names exist in the teamNameMap
 * Returns array of invalid team names
 */
function findInvalidTeamNames(winners: string[]): string[] {
  const invalidTeams: string[] = [];
  
  for (const winner of winners) {
    // FIX: Add comprehensive null/undefined/type checks
    if (!winner || 
        typeof winner !== 'string' || 
        winner.trim() === '' || 
        winner.trim() === 'xxx') {
      continue; // Skip empty or placeholder entries
    }
    
    const normalizedName = normalizeTeamNameForComparison(winner);
    
    // Check if the normalized name exists in teamNameMap
    let isValid = false;
    for (const key in teamNameMap) {
      if (normalizeTeamNameForComparison(key) === normalizedName) {
        isValid = true;
        break;
      }
    }
    
    if (!isValid) {
      invalidTeams.push(winner);
    }
  }
  
  return invalidTeams;
}

/**
 * Validates the bracket structure for consistency
 */
function validateBracketStructure(winners: string[]): { isValid: boolean; errorMessage?: string } {
  // Skip empty placeholders
  const validWinners = winners.map(w => w === 'xxx' ? '' : w);
  
  // Check each game that should depend on previous winners
  const inconsistencies = findBracketInconsistencies(validWinners);
  
  if (inconsistencies.length > 0) {
    const gameNum = inconsistencies[0].gameId + 1; // Convert to 1-based for display
    return {
      isValid: false,
      errorMessage: `Game ${gameNum} has winner ${inconsistencies[0].winner} which was not a winner in previous round`
    };
  }
  
  return { isValid: true };
}

/**
 * Interface for tracking bracket inconsistencies
 */
interface Inconsistency {
  gameId: number;
  winner: string;
  expectedPossibleWinners: string[];
}

/**
 * Gets the prerequisite games that must be won for a team to advance to a given game
 */
function getPrerequisiteGames(gameId: number): number[] {
  // First round games (0-31) have no prerequisites
  if (gameId < 32) return [];
  
  // Round of 32 (games 32-47) - prerequisite games are first round
  if (gameId >= 32 && gameId < 48) {
    const baseGame = (gameId - 32) * 2;
    return [baseGame, baseGame + 1];
  }
  
  // Sweet 16 (games 48-55) - prerequisite games are Round of 32
  if (gameId >= 48 && gameId < 56) {
    const baseGame = 32 + (gameId - 48) * 2;
    return [baseGame, baseGame + 1];
  }
  
  // Elite 8 (games 56-59) - prerequisite games are Sweet 16
  if (gameId >= 56 && gameId < 60) {
    const baseGame = 48 + (gameId - 56) * 2;
    return [baseGame, baseGame + 1];
  }
  
  // Final Four (games 60-61) - prerequisite games are Elite 8
  if (gameId >= 60 && gameId < 62) {
    const baseGame = 56 + (gameId - 60) * 2;
    return [baseGame, baseGame + 1];
  }
  
  // Championship (game 62) - prerequisite games are Final Four
  if (gameId === 62) {
    return [60, 61];
  }
  
  return [];
}

/**
 * Checks if a team is viable for a specific game position based on prerequisite wins
 */
function isTeamViableForPosition(team: string, gameId: number, winners: string[]): boolean {
  // First round teams are always viable (they're seeded)
  if (gameId < 32) return true;
  
  // Get the prerequisite games for this position
  const prereqGames = getPrerequisiteGames(gameId);
  if (prereqGames.length === 0) return true;
  
  // Normalize the team name for comparison
  const normalizedTeam = normalizeTeamNameForComparison(team);
  
  // Check if this team won at least one of the prerequisite games
  return prereqGames.some(prereqGameId => {
    if (prereqGameId >= winners.length) return false;
    const prereqWinner = winners[prereqGameId];
    if (!prereqWinner) return false;
    
    return normalizeTeamNameForComparison(prereqWinner) === normalizedTeam;
  });
}

/**
 * Find inconsistencies in the bracket structure
 */
function findBracketInconsistencies(winners: string[]): Inconsistency[] {
  const inconsistencies: Inconsistency[] = [];
  
  // Check Round of 32 (games 32-47)
  for (let i = 0; i < 16; i++) {
    const gameId = i + 32;
    const winner = winners[gameId];
    
    if (!winner) continue; // Skip empty games
    
    // Get possible winners from previous round
    const prevGame1 = i * 2;
    const prevGame2 = i * 2 + 1;
    const possibleWinners = [winners[prevGame1], winners[prevGame2]].filter(w => w);
    
    // If we have no possible winners but this game has a winner, it's inconsistent
    if (possibleWinners.length === 0) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: []
      });
      continue;
    }
    
    // If we have possible winners and current winner isn't among them, it's inconsistent
    if (!isPossibleWinner(winner, possibleWinners)) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: possibleWinners
      });
    }
  }
  
  // Check Sweet 16 (games 48-55)
  for (let i = 0; i < 8; i++) {
    const gameId = i + 48;
    const winner = winners[gameId];
    
    if (!winner) continue;
    
    const prevGame1 = i * 2 + 32;
    const prevGame2 = i * 2 + 33;
    const possibleWinners = [winners[prevGame1], winners[prevGame2]].filter(w => w);
    
    // If we have no possible winners but this game has a winner, it's inconsistent
    if (possibleWinners.length === 0) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: []
      });
      continue;
    }
    
    if (!isPossibleWinner(winner, possibleWinners)) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: possibleWinners
      });
    }
  }
  
  // Check Elite 8 (games 56-59)
  for (let i = 0; i < 4; i++) {
    const gameId = i + 56;
    const winner = winners[gameId];
    
    if (!winner) continue;
    
    const prevGame1 = i * 2 + 48;
    const prevGame2 = i * 2 + 49;
    const possibleWinners = [winners[prevGame1], winners[prevGame2]].filter(w => w);
    
    // If we have no possible winners but this game has a winner, it's inconsistent
    if (possibleWinners.length === 0) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: []
      });
      continue;
    }
    
    if (!isPossibleWinner(winner, possibleWinners)) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: possibleWinners
      });
    }
  }
  
  // Check Final Four (games 60-61)
  for (let i = 0; i < 2; i++) {
    const gameId = i + 60;
    const winner = winners[gameId];
    
    if (!winner) continue;
    
    const prevGame1 = i * 2 + 56;
    const prevGame2 = i * 2 + 57;
    const possibleWinners = [winners[prevGame1], winners[prevGame2]].filter(w => w);
    
    // If we have no possible winners but this game has a winner, it's inconsistent
    if (possibleWinners.length === 0) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: []
      });
      continue;
    }
    
    if (!isPossibleWinner(winner, possibleWinners)) {
      inconsistencies.push({
        gameId,
        winner,
        expectedPossibleWinners: possibleWinners
      });
    }
  }
  
  // Check Championship (game 62)
  const championshipWinner = winners[62];
  if (championshipWinner) {
    const possibleWinners = [winners[60], winners[61]].filter(w => w);
    
    // If we have no possible winners but championship has a winner, it's inconsistent
    if (possibleWinners.length === 0) {
      inconsistencies.push({
        gameId: 62,
        winner: championshipWinner,
        expectedPossibleWinners: []
      });
    } else if (!isPossibleWinner(championshipWinner, possibleWinners)) {
      inconsistencies.push({
        gameId: 62,
        winner: championshipWinner,
        expectedPossibleWinners: possibleWinners
      });
    }
  }
  
  return inconsistencies;
}

/**
 * Helper function to check if a team is a possible winner based on normalized comparison
 */
function isPossibleWinner(winner: string, possibleWinners: string[]): boolean {
  const normalizedWinner = normalizeTeamNameForComparison(winner);
  
  return possibleWinners.some(possible => 
    normalizeTeamNameForComparison(possible) === normalizedWinner
  );
}

/**
 * Fix bracket inconsistencies by clearing invalid winners
 * Enhanced version that checks team viability before processing inconsistencies
 */
function fixBracketInconsistencies(winners: string[]): string[] {
  const result = [...winners];
  
  // ENHANCED: First pass - Check team viability for all games beyond first round
  for (let gameId = 32; gameId < result.length; gameId++) {
    const winner = result[gameId];
    if (!winner || winner.trim() === '' || winner === 'xxx') continue;
    
    // Check if this team is viable for this position
    if (!isTeamViableForPosition(winner, gameId, result)) {
      result[gameId] = '';
      
      // Clear any dependent games that depended on this invalid winner
      clearDependentGames(result, gameId);
    }
  }
  
  // EXISTING: Second pass - Find and fix remaining bracket inconsistencies
  const inconsistencies = findBracketInconsistencies(result);
  
  // Process inconsistencies in reverse game order (from championship to earlier rounds)
  inconsistencies.sort((a, b) => b.gameId - a.gameId);
  
  for (const inconsistency of inconsistencies) {
    // Clear the inconsistent winner
    result[inconsistency.gameId] = '';
    
    // Clear any dependent games (games that depend on this winner)
    clearDependentGames(result, inconsistency.gameId);
  }
  
  return result;
}

/**
 * Clear any games that depend on the specified game
 */
function clearDependentGames(winners: string[], gameId: number): void {
  // Round of 32 feeds into Sweet 16
  if (gameId >= 32 && gameId < 48) {
    const nextGameId = 48 + Math.floor((gameId - 32) / 2);
    
    // If the next game's winner is the same as this game's winner, clear it
    if (winners[nextGameId] && normalizeTeamNameForComparison(winners[nextGameId]) === normalizeTeamNameForComparison(winners[gameId])) {
      winners[nextGameId] = '';
      clearDependentGames(winners, nextGameId);
    }
  }
  
  // Sweet 16 feeds into Elite 8
  else if (gameId >= 48 && gameId < 56) {
    const nextGameId = 56 + Math.floor((gameId - 48) / 2);
    
    if (winners[nextGameId] && normalizeTeamNameForComparison(winners[nextGameId]) === normalizeTeamNameForComparison(winners[gameId])) {
      winners[nextGameId] = '';
      clearDependentGames(winners, nextGameId);
    }
  }
  
  // Elite 8 feeds into Final Four
  else if (gameId >= 56 && gameId < 60) {
    const nextGameId = 60 + Math.floor((gameId - 56) / 2);
    
    if (winners[nextGameId] && normalizeTeamNameForComparison(winners[nextGameId]) === normalizeTeamNameForComparison(winners[gameId])) {
      winners[nextGameId] = '';
      clearDependentGames(winners, nextGameId);
    }
  }
  
  // Final Four feeds into Championship
  else if (gameId >= 60 && gameId < 62) {
    if (winners[62] && normalizeTeamNameForComparison(winners[62]) === normalizeTeamNameForComparison(winners[gameId])) {
      winners[62] = '';
    }
  }
}

/**
 * When setting up for display, this is useful to have a fixed number of elements in the array
 */
export function ensureFullLength(winners: string[]): string[] {
  if (winners.length < 63) {
    // Fill any missing positions with empty strings
    const missingCount = 63 - winners.length;
    return [...winners, ...Array(missingCount).fill('')];
  }
  return winners;
}